import { useEffect, useRef, useState } from "react";
import { formatWeekdayDateOrDash } from "@shared-core/domain/formatters";
import { formatPriceOrNotYetPriced } from "@shared-core/domain/checkout-delivery";
import type {
  ArtworkWrites,
  OrderLineView,
  OrderPageView,
  ProofView,
  VersionView,
} from "@shared-core/data/customer-link-adapter";
import { Card } from "../../app/components/primitives/card";
import { Badge } from "../../app/components/primitives/badge";
import { Button } from "../../app/components/primitives/button";
import { cn } from "../../app/components/primitives/utils";
import { ArrowRight, ChevronLeft } from "lucide-react";
import { ArtworkUploadSlots } from "./ArtworkUploadSlots";
import { DeliveryLink } from "./DeliveryLink";
import { ProofDecisionForm } from "./ProofDecisionForm";
import {
  ProofMadeFrom,
  ProofOutcome,
  ProofPdf,
  type ProofPdfSource,
} from "./ProofReview";
import { ATTENTION_TINT, AttentionDot } from "./attention";
import { ChosenOptions } from "./ChosenOptions";
import { ItemHistory } from "./ItemHistory";
import { ItemThumbnail } from "./ItemThumbnail";
import { ItemSubTabs, type ItemPane } from "./ItemSubTabs";
import { usePortalPageSlots } from "../portal/PortalPageSlots";

// One item of the order (the Items tab's detail, Ben 5 Oct): its spec,
// quantity and versions; the next-step line (doc 39 §7); then, when the item
// needs artwork, per version and part the files and their check results, the
// proof to decide (on its own, with the worth-knowing ticks, or changes with
// a reason), superseded proofs and its history. A proof opens in place
// (`?proof=`); deciding it offers the next proof waiting, wherever it is.
// Nothing here knows how the page was reached: the link or a sign-in.

export interface ItemDetailActions extends ProofPdfSource {
  writes: ArtworkWrites;
  reload: () => Promise<void>;
  openProof: (proofId: string) => void;
  closeProof: () => void;
  openDelivery: () => void;
}

// The version header carries a status dot only (Ben, 5 Oct): the check
// result is said once, in its box, and a proof's state on its own row. A
// stage that needs the customer takes the attention dot; the others a quiet
// status dot (held and checking are neutral, never the attention dot).
const STAGE_DOT: Record<VersionView["stage"], string | null> = {
  files_owed: null,
  checking: "bg-gray-400",
  cant_print: null,
  held: "bg-gray-400",
  checked: "bg-gray-400",
  proof_out: null,
  changes_asked: "bg-gray-400",
  approved: "bg-green-600",
};

const STATE_WORDS: Record<ProofView["state"], string> = {
  out: "waiting",
  approved: "approved",
  rejected: "changes asked for",
  superseded: "replaced",
};

function VersionBlock({
  line,
  version,
  isFocused,
  actions,
}: {
  line: OrderLineView;
  version: VersionView;
  /** Opened from a Needs you row: bring this version into view. */
  isFocused: boolean;
  actions: ItemDetailActions;
}) {
  const proof = version.current_proof;
  const needsFiles = ["files_owed", "cant_print", "held", "checking"].includes(
    version.stage,
  );
  const needsYou =
    STAGE_DOT[version.stage] === null &&
    (version.slots.some((s) => s.upload_url) ||
      version.name_url !== null ||
      proof?.decide_url != null);
  const slots = usePortalPageSlots();
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (isFocused) ref.current?.scrollIntoView({ block: "center" });
  }, [isFocused]);
  return (
    <div
      ref={ref}
      id={`artwork-${version.version_id}`}
      className={cn(
        "space-y-3 rounded-lg border p-3",
        isFocused && "ring-2 ring-primary/50",
      )}
    >
      <p className="flex items-center gap-2 text-sm font-medium">
        {needsYou ? (
          <AttentionDot label={`Needs you: ${version.status_label}`} />
        ) : (
          <span
            className={cn(
              "size-2 shrink-0 rounded-full",
              STAGE_DOT[version.stage] ?? "bg-gray-400",
            )}
            role="img"
            aria-label={version.status_label}
          />
        )}
        {version.display
          ? `Version ${version.display}`
          : line.parts_label
            ? `Your files (${line.parts_label})`
            : "Your file"}
      </p>
      {needsFiles && (
        <ArtworkUploadSlots
          version={version}
          writes={actions.writes}
          onChanged={actions.reload}
          fetchFile={actions.fetchFile}
          idleHint="We'll send you a proof as soon as it's in."
        />
      )}
      {version.stage === "checked" && (
        <p className="text-sm text-muted-foreground">{version.status_label}</p>
      )}
      {slots.orderVersion?.(version.version_id)}
      {proof && (
        <div
          className={cn(
            "flex flex-wrap items-center justify-between gap-2 rounded-md px-3 py-2",
            proof.decide_url ? ATTENTION_TINT : "bg-muted/30",
          )}
        >
          <span className="text-sm">
            <span className="font-medium">{proof.label}</span>{" "}
            <span className="text-muted-foreground">· {proof.status_line}</span>
          </span>
          <Button
            size="sm"
            variant="outline"
            onClick={() => actions.openProof(proof.id)}
          >
            {proof.decide_url ? "Review" : "View"}
          </Button>
        </div>
      )}
      {proof && slots.orderProof?.(proof.id)}
      {version.stage === "changes_asked" && proof?.comment && (
        <p className="text-sm text-muted-foreground">
          Your comments are with our studio:{" "}
          <span className="italic">&ldquo;{proof.comment}&rdquo;</span>{" "}
          We&apos;ll send you a new proof.
        </p>
      )}
      {version.proofs.length > (proof ? 1 : 0) && (
        <p className="text-xs text-muted-foreground">
          Earlier:{" "}
          {version.proofs
            .filter((p) => p.id !== proof?.id)
            .map((p) => `${p.label} ${STATE_WORDS[p.state]}`)
            .join(" · ")}
        </p>
      )}
    </div>
  );
}

function ProofInPlace({
  page,
  line,
  version,
  proof,
  actions,
}: {
  page: OrderPageView;
  line: OrderLineView;
  version: VersionView;
  proof: ProofView;
  actions: ItemDetailActions;
}) {
  const [justDecided, setJustDecided] = useState(false);
  const [isReplacing, setIsReplacing] = useState(false);
  const slots = usePortalPageSlots();

  useEffect(() => {
    setJustDecided(false);
    setIsReplacing(false);
    if (proof.opened_url && !proof.is_opened) {
      void actions.writes
        .markProofOpened(proof.opened_url)
        .then(actions.reload);
    }
    // Marking a proof opened is a side effect of first showing it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proof.id]);

  const heading = `${line.ref} ${line.title}${version.display ? ` · ${version.display}` : ""}`;
  const waiting = page.waiting_proof_ids;
  const position = waiting.indexOf(proof.id);
  const nextId = waiting.find((id) => id !== proof.id) ?? null;
  const earlier = version.proofs.filter((p) => p.id !== proof.id);
  const isCurrent = version.current_proof?.id === proof.id;
  const decideUrl = proof.decide_url;
  const replaceUpload = version.slots.find((s) => s.upload_url);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Button
          variant="ghost"
          size="sm"
          className="-ml-2"
          onClick={actions.closeProof}
        >
          <ChevronLeft className="size-4" />
          Back to {line.ref} {line.title}
        </Button>
        {position >= 0 && waiting.length > 1 && (
          <span className="text-sm text-muted-foreground">
            {position + 1} of {waiting.length} waiting
          </span>
        )}
      </div>
      <div className="space-y-1">
        <h3 className="text-lg font-semibold">
          {heading} · {proof.label}
        </h3>
        <p className="text-sm text-muted-foreground">{proof.status_line}</p>
      </div>
      {slots.orderProof?.(proof.id)}

      <ProofPdf
        proof={proof}
        title={`${heading} · ${proof.label}`}
        source={actions}
      />

      <ProofMadeFrom proof={proof} />

      {decideUrl && !justDecided && page.wording_version !== null ? (
        <ProofDecisionForm
          idPrefix={`proof-${proof.id}`}
          approveConsequence="Approving means we print it exactly as shown here. Each item goes to print once all its proofs are approved."
          findingsToAccept={proof.findings.filter(
            (f) => f.tier === "worth_knowing",
          )}
          reasonOptions={proof.reason_options}
          wordingVersion={page.wording_version}
          onSubmit={async (decision) => {
            await actions.writes.decideProof(decideUrl, decision);
            setJustDecided(true);
            await actions.reload();
          }}
        />
      ) : (
        <ProofOutcome proof={proof} />
      )}

      {justDecided && (
        <Card className="flex-row flex-wrap items-center justify-between gap-3 border-primary/30 bg-primary/5 p-4">
          {nextId ? (
            <>
              <p className="text-sm font-medium">
                {waiting.length === 1
                  ? "One more proof is waiting for you."
                  : `${waiting.length} more proofs are waiting for you.`}
              </p>
              <Button
                size="sm"
                variant="outline"
                onClick={() => actions.openProof(nextId)}
              >
                Next proof
                <ArrowRight className="size-4" />
              </Button>
            </>
          ) : (
            <>
              <p className="text-sm font-medium">
                That&apos;s every proof for now. Thank you.
              </p>
              <Button size="sm" variant="outline" onClick={actions.closeProof}>
                Back to {line.ref} {line.title}
              </Button>
            </>
          )}
        </Card>
      )}

      {!justDecided && nextId && !decideUrl && (
        <div className="flex justify-end">
          <Button
            size="sm"
            variant="outline"
            onClick={() => actions.openProof(nextId)}
          >
            Next proof waiting
            <ArrowRight className="size-4" />
          </Button>
        </div>
      )}

      {isCurrent && decideUrl && replaceUpload && (
        <div className="space-y-2 border-t pt-4">
          {isReplacing ? (
            <ArtworkUploadSlots
              version={{ ...version, needs_name: false }}
              writes={actions.writes}
              onChanged={async () => {
                await actions.reload();
                actions.closeProof();
              }}
              fetchFile={actions.fetchFile}
              idleHint=""
            />
          ) : (
            <p className="text-sm text-muted-foreground">
              Rather send us a new file instead?{" "}
              <Button
                variant="link"
                className="h-auto p-0"
                onClick={() => setIsReplacing(true)}
              >
                Upload a new file
              </Button>{" "}
              and we&apos;ll proof that instead.
            </p>
          )}
        </div>
      )}

      {earlier.length > 0 && (
        <div className="space-y-2 border-t pt-4">
          <p className="text-sm font-medium">
            Earlier proofs of this {version.display ? "version" : "item"}
          </p>
          <ol className="space-y-2">
            {earlier.map((p) => (
              <li
                key={p.id}
                className="space-y-0.5 border-l-2 border-muted pl-3 text-sm"
              >
                <p>
                  <span className="font-medium">{p.label}</span>{" "}
                  <Badge
                    variant={p.state === "approved" ? "success" : "neutral"}
                  >
                    {
                      {
                        out: "Waiting",
                        approved: "Approved",
                        rejected: "Changes asked for",
                        superseded: "Replaced",
                      }[p.state]
                    }
                  </Badge>
                </p>
                <p className="text-xs text-muted-foreground">{p.status_line}</p>
                {p.comment && (
                  <p className="text-xs italic text-muted-foreground">
                    &ldquo;{p.comment}&rdquo;
                  </p>
                )}
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}

export function OrderItemDetail({
  page,
  line,
  proofId,
  focusVersionId,
  pane,
  onPane,
  actions,
}: {
  page: OrderPageView;
  line: OrderLineView;
  proofId: string | null;
  focusVersionId: string | null;
  /** The sub-tab the URL asks for; null: Artwork when it needs something. */
  pane: ItemPane | null;
  onPane: (pane: ItemPane) => void;
  actions: ItemDetailActions;
}) {
  const slots = usePortalPageSlots();
  const artwork = line.artwork;
  const current: ItemPane =
    artwork === null
      ? "details"
      : proofId || focusVersionId
        ? "artwork"
        : (pane ?? (line.artwork_needed > 0 ? "artwork" : "details"));
  const open = proofId
    ? (artwork ?? []).flatMap((v) =>
        v.proofs
          .filter((p) => p.id === proofId)
          .map((p) => ({ version: v, proof: p })),
      )[0]
    : undefined;
  if (open) {
    return (
      <ProofInPlace
        page={page}
        line={line}
        version={open.version}
        proof={open.proof}
        actions={actions}
      />
    );
  }
  const history = (artwork ?? [])
    .flatMap((v) => v.history)
    .sort((a, b) => b.at.localeCompare(a.at));
  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3">
        <ItemThumbnail name={line.title} family={line.spec} />
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-lg font-semibold">
              <span className="text-muted-foreground tabular-nums">
                {line.ref}
              </span>{" "}
              {line.title}
            </h3>
            {line.added_on && (
              <Badge variant="neutral">
                Added {formatWeekdayDateOrDash(line.added_on)}
              </Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground">{line.spec}</p>
          <ChosenOptions options={line.chosen_options} />
          {line.next_step && (
            <p className="text-sm font-medium">→ {line.next_step}</p>
          )}
          {line.when && <p className="text-sm">{line.when}</p>}
        </div>
      </div>

      <ItemSubTabs
        current={current}
        artworkNeeded={line.artwork_needed}
        hasArtwork={artwork !== null}
        onSelect={onPane}
      />

      {current === "details" || artwork === null ? (
        <div className="space-y-4">
          <dl className="grid grid-cols-[8rem_minmax(0,1fr)] gap-x-4 gap-y-2 text-sm">
            <dt className="text-muted-foreground">Quantity</dt>
            <dd>{line.quantity.toLocaleString("en-GB")}</dd>
            {line.parts_label && (
              <>
                <dt className="text-muted-foreground">Parts</dt>
                <dd>{line.parts_label}</dd>
              </>
            )}
            {line.versions.length > 1 && (
              <>
                <dt className="text-muted-foreground">Versions</dt>
                <dd>
                  {line.versions_note ?? (
                    <>
                      {line.versions.length}, split{" "}
                      {line.versions
                        .map(
                          (v) =>
                            `${v.label ?? ""}${v.name ? ` ${v.name}` : ""} ${(v.quantity ?? 0).toLocaleString("en-GB")}`,
                        )
                        .join(" · ")}
                    </>
                  )}
                </dd>
              </>
            )}
            <dt className="text-muted-foreground">Price</dt>
            <dd>
              {formatPriceOrNotYetPriced(line.price_pence)}
              <span className="text-muted-foreground">
                {" "}
                + {line.vat_label ?? "VAT"}
              </span>
            </dd>
          </dl>
          <DeliveryLink
            addressNames={line.deliveries.map((d) => d.address_name ?? "")}
            onOpen={actions.openDelivery}
          />
        </div>
      ) : (
        <div className="space-y-3">
          {line.approved_label && (
            <p className="text-sm text-muted-foreground">
              {line.approved_label}
            </p>
          )}
          {artwork.map((v) => (
            <VersionBlock
              key={v.version_id}
              line={line}
              version={v}
              isFocused={v.version_id === focusVersionId}
              actions={actions}
            />
          ))}
        </div>
      )}

      {current === "artwork" && <ItemHistory lines={history} />}
      {slots.orderLine?.(line.ref)}
    </div>
  );
}
